import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@remix-run/react';
import {
	GQLBrandFragment,
	GQLCategoryFragment,
	GQLColourGroupFragment,
	GQLOperationalSettingsFragment,
	GQLProductCollectionFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductTileFragment,
	GQLProductTypeType,
	GQLSizeFragment,
	GQLStoreAllowedDecorationType,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import { ImageSize, cn, mapEnumValue } from '@theprintbar/shared';
import { Accordion, ButtonFilter, CheckboxFilter, ColourFilter, DtgIcon, EmbIcon, ImageContainer, ScrIcon, SlideFilter } from '../../..';
import FilterItem from './FilterItem';
import { ProductQueryKeys, ProductSearchQuery, getProductSearchURL } from './productSearch';
import { getPriceLabel } from './utils/priceLabel';

export interface FilterListProps {
	expandAll: boolean;
	category?: GQLCategoryFragment | null;
	operationalSettings: GQLOperationalSettingsFragment;
	categories?: GQLCategoryFragment[] | null;
	minPrice?: Pick<GQLProductTileFragment, 'salePrice'>[] | null;
	maxPrice?: Pick<GQLProductTileFragment, 'salePrice'>[] | null;
	colours?: GQLColourGroupFragment[] | null;
	sizes?: GQLSizeFragment[] | null;
	brands?: GQLBrandFragment[] | null;
	productTypes?: GQLProductTypeType[] | null;
	params: ProductSearchQuery;
	collections?: GQLProductCollectionFragment[] | null;
	store?: GQLStoreFragment | null;
}

export const ProductFilterList = function ({
	expandAll,
	colours,
	sizes,
	brands,
	params,
	productTypes,
	operationalSettings,
	collections,
	store,
}: FilterListProps) {
	const navigate = useNavigate();
	const decorations = (
		store
			? store.allowedDecorations.map(d => mapEnumValue(GQLStoreAllowedDecorationType, GQLProductSideAvailableDecorationType, d))
			: [
					GQLProductSideAvailableDecorationType.Dtg,
					GQLProductSideAvailableDecorationType.Dtf,
					GQLProductSideAvailableDecorationType.Emb,
					GQLProductSideAvailableDecorationType.Scr,
			  ]
	).filter(Boolean) as GQLProductSideAvailableDecorationType[];

	return (
		<div>
			{decorations.length > 1 && (
				<FilterItem
					filterTitle="Decoration"
					activeLabel={params.decoration ? operationalSettings[`${params.decoration}Title`] : ''}
					defaultOpen
					expandAll={expandAll}
				>
					<div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
						{decorations.map(decoration => {
							const disabled = !!params[ProductQueryKeys.Sameday] && decoration !== GQLProductSideAvailableDecorationType.Dtg;
							const icon =
								decoration === GQLProductSideAvailableDecorationType.Dtg
									? DtgIcon
									: decoration === GQLProductSideAvailableDecorationType.Emb
									? EmbIcon
									: decoration === GQLProductSideAvailableDecorationType.Scr
									? ScrIcon
									: decoration === GQLProductSideAvailableDecorationType.Dtf
									? DtgIcon
									: null;
							return (
								<button
									type="button"
									key={decoration}
									disabled={disabled}
									onClick={() => navigate(getProductSearchURL(ProductQueryKeys.Decoration, decoration, params))}
									className={cn('mb-1 flex items-center text-left', disabled && 'text-grey opacity-50 pointer-events-none')}
								>
									{icon?.({
										isBackgroundBlue: params.decoration === decoration,
										className: 'mr-2',
									})}
									<span className="font-sans text-sm lg:text-base capitalize">
										{operationalSettings[`${decoration}Title`]}
									</span>
								</button>
							);
						})}
						{!!params[ProductQueryKeys.Sameday] && (
							<p className="mt-2 italic text-xs text-grey">Sameday is only available with Digital printing.</p>
						)}
					</div>
				</FilterItem>
			)}
			{!!collections && (
				<FilterItem
					className="lg:hidden"
					filterTitle="Collections"
					activeLabel={collections.find(c => c.code === params[ProductQueryKeys.Collection])?.name}
					expandAll={expandAll}
				>
					<div className="flex flex-wrap gap-2">
						{collections?.map(({ id, icon, name, colour, code }) => (
							<button
								type="button"
								key={id}
								onClick={() => navigate(getProductSearchURL(ProductQueryKeys.Collection, code, params))}
								className={cn(
									'h-min rounded-full px-3 py-1 flex items-center hover:opacity-80 uppercase text-blue border border-blue font-belfast font-bold text-xs',
									params.collection === code && 'ring-2 ring-blue',
								)}
								style={{ backgroundColor: colour || '' }}
							>
								{name}
								<ImageContainer size={ImageSize.Tiny} image={icon} className="w-4 h-4 ml-2" />
							</button>
						))}
					</div>
				</FilterItem>
			)}

			{/* Type Checkbox */}
			{!!productTypes && (
				<FilterItem filterTitle="Type" activeLabel={params[ProductQueryKeys.Type]} expandAll={expandAll}>
					<div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
						{productTypes.map(type => {
							return (
								<CheckboxFilter
									key={type}
									label={type}
									active={params.type === type}
									url={getProductSearchURL(ProductQueryKeys.Type, type, params)}
								/>
							);
						})}
					</div>
				</FilterItem>
			)}

			{/* Brand Checkbox */}
			{!!brands && (
				<FilterItem filterTitle="Brand" activeLabel={brands.find(b => b.code === params.brand)?.name} expandAll={expandAll}>
					<div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
						{brands
							.sort((a, b) => {
								//Sort by .name
								return a.name.localeCompare(b.name);
							})
							.map(({ name, code }) => {
								return (
									<CheckboxFilter
										key={code}
										label={name}
										active={params.brand === code}
										url={getProductSearchURL(ProductQueryKeys.Brand, code, params)}
									/>
								);
							})}
					</div>
				</FilterItem>
			)}

			{/* Colours Selection */}
			{!!colours && (
				<FilterItem filterTitle="Colour" activeLabel={params[ProductQueryKeys.Color]} expandAll={expandAll}>
					<div className="grid grid-cols-3 lg:grid-cols-2 gap-x-4 gap-y-2">
						{colours.map(({ hex, name, secondaryHex }) => (
							<ColourFilter
								name={name}
								hex={hex}
								secondaryHex={secondaryHex}
								key={hex}
								active={params.color === name}
								url={getProductSearchURL(ProductQueryKeys.Color, name, params)}
							/>
						))}
					</div>
				</FilterItem>
			)}
			{/* Price Slider */}
			<FilterItem filterTitle="Price" activeLabel={getPriceLabel(params[ProductQueryKeys.Price])} expandAll={expandAll}>
				<div className="pr-2">
					<SlideFilter
						urlGenerator={values => {
							const [min, max] = values;
							if (min === 0 && max === 100) return getProductSearchURL(ProductQueryKeys.Price, undefined, params);
							return getProductSearchURL(ProductQueryKeys.Price, values, params);
						}}
						active={params.price !== undefined}
						min={0}
						max={100}
					/>
				</div>
			</FilterItem>

			{/* Size Buttons */}
			{!!sizes && (
				<FilterItem filterTitle="Size" activeLabel={params[ProductQueryKeys.Size]} expandAll={expandAll}>
					<div className="flex flex-wrap gap-2">
						{sizes
							.sort((a, b) => {
								return a.order - b.order;
							})
							.map(({ code }) => (
								<ButtonFilter
									key={code}
									label={code}
									active={params.size === code}
									url={getProductSearchURL(ProductQueryKeys.Size, code, params)}
								/>
							))}
					</div>
				</FilterItem>
			)}

			{/* Fit and Features will come later */}

			<button
				type="button"
				onClick={() => navigate('?')}
				className="border-t border-blue w-full flex items-center py-3 lg:py-4 text-sm lg:text-base"
			>
				<ArrowPathIcon className="h-5 w-5 mr-2" />
				Clear All
			</button>
		</div>
	);
};
